// pages/account.js — Account page.
//
// Owns the `data-page="account"` section. Cloud-only (forks to
// daemon mode rendering an Identity panel via cached whoami data).
// Pulls Supabase user → email, display_name, avatar; refreshes
// connected accounts (OAuth identities), MFA status, recovery codes.
//
// Migration note: `supabase` is the only thing that's NOT a function
// in the bridge — it's a `let`-declared reference reassigned during
// init. We bridge it via getSupabase() so the lookup happens at call
// time and we always see the current binding. renderAccountIdentity-
// Panel is also bridge-bound because it still lives in bootstrap.js's
// auth closure (reads supabase + state.token directly).
//
// The 4 refresh* helpers all hoisted to their own modules in Phase K
// Tier 2 (account-avatar.js, account-auth.js) and are now direct
// imports rather than bridge consumers (Tier 2n cleanup).
//
// Per Settings: Account also tears down inputs if re-rendered mid-
// edit (display name field, etc.). So we do an initial render on
// start() but don't subscribe to dashboard:tick.
import { $ } from "../dom.js";
import { refreshAccountAvatarPreview } from "../account-avatar.js";
import {
  refreshConnectedAccounts,
  refreshAccountMfaStatus,
  refreshRecoveryCodesStatus,
} from "../account-auth.js";

// AC_LEGACY is populated by bootstrap.js before the bundle's page
// modules run (the side-effect import at the top of main.js
// guarantees it). getSupabase + renderAccountIdentityPanel still
// resolve at call time so we see the up-to-date bindings.
async function renderAccountPage() {
  if (!window.AC_LEGACY) {
    throw new Error(
      "[account] window.AC_LEGACY not populated — bootstrap.js init order broken",
    );
  }
  const { getSupabase, renderAccountIdentityPanel } = window.AC_LEGACY;

  const supabase = getSupabase();

  // Daemon mode: render the Identity panel (the cloud-only
  // Profile/Password/2FA/Sessions panels are hidden via CSS).
  // The panel reuses the cached whoami data — same source as the
  // sidebar badge.
  if (!supabase) {
    renderAccountIdentityPanel();
    return;
  }

  // Profile: email (read-only) + display name (editable).
  let user = null;
  try {
    const resp = await supabase.auth.getUser();
    user = resp.data && resp.data.user;
  } catch {
    // best-effort
  }
  if (user) {
    if ($("account-email"))
      $("account-email").textContent = user.email || "--";
    const dn =
      (user.user_metadata &&
        (user.user_metadata.display_name || user.user_metadata.full_name)) ||
      "";
    if ($("account-display-name-input"))
      $("account-display-name-input").value = dn;
  }

  // Avatar preview: shows current uploaded avatar, Gravatar
  // fallback, or initial-letter SVG. Same precedence as the
  // sidebar badge.
  await refreshAccountAvatarPreview(user);

  // Connected accounts (OAuth identities). Best-effort — failures
  // paint an error row but don't block the rest of the page.
  refreshConnectedAccounts().catch(() => {});

  // 2FA status — list factors and pick a verified TOTP, if any.
  await refreshAccountMfaStatus();
  // Recovery codes count (only meaningful if MFA is enrolled).
  await refreshRecoveryCodesStatus();
  // Sessions panel is render-once (no async data) — buttons are
  // wired at script init. Nothing to populate here.
}

// ---- mount ----

let started = false;

function start() {
  if (started) return;
  started = true;
  // Account, like Settings, is a static-ish page where dashboard.js
  // skips the poll-tick render. Do the initial render here. Don't
  // subscribe to dashboard:tick so user input isn't clobbered.
  renderAccountPage();
}

function stop() {
  if (!started) return;
  started = false;
}

function bindLifecycle() {
  const section = document.querySelector('section.page[data-page="account"]');
  if (!section) return;
  if (section.classList.contains("active")) start();
  const obs = new MutationObserver((mutations) => {
    mutations.forEach((m) => {
      if (m.attributeName !== "class") return;
      if (section.classList.contains("active")) start();
      else stop();
    });
  });
  obs.observe(section, { attributes: true });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", bindLifecycle);
} else {
  bindLifecycle();
}
